import {
    EuiTable,
    EuiTableBody,
    EuiTableHeader,
    EuiTableHeaderCell,
    EuiLink,
    EuiTableRowCellCheckbox,
    EuiCheckbox,
    EuiButtonIcon,
    EuiContextMenuItem,
    EuiContextMenuPanel,
    EuiPopover,
    EuiTableRow,
    EuiTableRowCell,
    EuiTableHeaderCellCheckbox,
    EuiTablePagination,
    EuiLoadingContent,
    EuiTextColor,
    EuiTableHeaderMobile,
    EuiFlexGroup,
    EuiFlexItem,
    EuiTableSortMobile,
    EuiSpacer,
    EuiTitle,
} from "@elastic/eui";
import { Pager } from "@elastic/eui/lib/services/paging";
import { SortableProperties } from "@elastic/eui/lib/services/sort";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { Link, useHistory } from "react-router-dom";
import moment from "moment";
import { useIntl } from "react-intl";
import { mapResourceObjectToArray } from "../../../../utils/jsonApiUtils";

import { fetchEntities, destroyEntity, entitySelector } from "../../../shared/entity/slice";
import defaultColumns from "../../configs/columns";

import * as S from "../../style";

import ConfirmationModal from "../../../shared/modals/ConfirmationModal";
import useQueryString from "../../../../hooks/useQueryString";
import { parseSortParams } from "../../../../utils/tableQueryParams";
import { sortPointsByPosition } from "../../utils";
import TableFilter from "../../../shared/TableFilter";
import tripFilterFields from "../../configs/filterFields";
import { clearCommentStates } from "../../../shared/Comments/commentSlice";
import TableGroup from "../../../shared/TableGroup";
import groupFields from "../../configs/groupFields";

import useDidUpdateEffect from "../../../../hooks/useDidUpdateEffect";
import TripColumnsSelector from "./TripColumnsSelector";
import Thumbnail from "../../../shared/Thumbnail/Thumbnail";
import { isValidDate } from "../../../../utils/dateAndTime";
import { isSuperAdmin, userIdSelector, logoutSuccess } from "../../../auth/slice";

const initialSortablePropertyName = "id";
const initialItemsPerPage = 10;
const tableId = "tripsTable";

const useDefaultFilter = () => {
    const isCurrentUserSuperAdmin = useSelector(isSuperAdmin());
    const userId = useSelector(userIdSelector());

    if (isCurrentUserSuperAdmin) {
        return {
            createdAt: [
                moment().subtract(7, "d").startOf("day").locale("en").format(),
                moment().endOf("day").locale("en").format(),
            ],
        };
    }
    return {
        admin: [userId],
        createdAt: [
            moment().subtract(7, "d").startOf("day").locale("en").format(),
            moment().endOf("day").locale("en").format(),
        ],
    };
};

const generateSortOptions = (columns) =>
    columns
        .filter((column) => column.isSortable)
        .map((column) => ({ name: column.id, isAscending: false }));

let sortableProperties;
let pager;
const initParamHelpers = () => {
    sortableProperties = new SortableProperties(
        [...generateSortOptions(defaultColumns)],
        initialSortablePropertyName
    );
    pager = new Pager(initialItemsPerPage, initialItemsPerPage);
};
initParamHelpers();

const generateSortString = (sortField, isAscending) => (isAscending ? sortField : `-${sortField}`);

const renderColumn = ({ colValue, option }) => {
    if (!_.isNil(colValue)) {
        if (Object.keys(colValue).findIndex((k) => k.toLocaleLowerCase() === "url") !== -1) {
            return (
                <Thumbnail
                    src={colValue.url}
                    url={colValue.url}
                    alt={option.label}
                />
            );
        }
        if (option.id.toLocaleLowerCase() === "id") {
            return <Link to={`trips/${colValue}`}>#{colValue}</Link>;
        }
        if (option.id.toLocaleLowerCase() === "name") {
            return <b>{colValue}</b>;
        }
        if (isValidDate(colValue)) {
            return <>{moment(colValue).format("DD MMM YYYY h:mm")}</>;
        }
        return <>{`${colValue}`}</>;
    }

    return "N/A";
};

const dropdownOptionsToColumns = (selectedOptions, defaultColumns) => {
    const actionsColumn = defaultColumns.find((column) => column.id === "tripActions");

    return [
        ...selectedOptions.map((option) => {
            const defaultColumn = defaultColumns.find((column) => column.id === option.id);

            return {
                id: option.id,
                label: defaultColumn ? defaultColumn.label : _.startCase(option.label),
                isSortable: defaultColumn ? defaultColumn.isSortable : true,
                width: defaultColumn ? defaultColumn.width : "auto",
                truncateText: true,
                render: (colValue, entity, trip, fetchTrips) =>
                    defaultColumn
                        ? defaultColumn.render(colValue, entity, trip, fetchTrips)
                        : renderColumn({
                              colValue,
                              option,
                          }),
            };
        }),
        actionsColumn,
    ];
};

const includedEntities = [
    "driver",
    "truck",
    "tripPoints",
    "organization",
    "truckType",
    "supplier",
    "creator",
    "tripType",
];

function TripTable({ url, relationKey }) {
    const dispatch = useDispatch();
    const intl = useIntl();

    const userIdKey = useSelector(userIdSelector());

    const tripsSlice = useSelector(entitySelector("trips"));

    const trips = mapResourceObjectToArray(tripsSlice.data);
    const drivers = mapResourceObjectToArray(tripsSlice.included.drivers);
    const suppliers = mapResourceObjectToArray(tripsSlice.included.suppliers);
    const tripPoints = sortPointsByPosition(
        mapResourceObjectToArray(tripsSlice.included.tripPoints)
    );
    const trucks = mapResourceObjectToArray(tripsSlice.included.trucks);
    const organizations = mapResourceObjectToArray(tripsSlice.included.organizations);
    const tripTypes = mapResourceObjectToArray(tripsSlice.included.tripTypes);

    const [columns, setColumns] = useState(defaultColumns);

    // eslint-disable-next-line no-unused-vars
    const [totalRecordCount, setTotalRecordCount] = useState(tripsSlice.meta?.totalRecordCount);

    const isNested = !!url || !!relationKey;
    const includedEntitiesString = includedEntities.join(",");

    const [pageIndex, setPageIndex] = useQueryString("page[number]", 1, {
        disableUrlParams: isNested,
    });
    const [itemsPerPage, setItemsPerPage] = useQueryString("page[size]", initialItemsPerPage, {
        disableUrlParams: isNested,
    });
    const [groupQuery, setGroupQuery] = useQueryString("group", "", {
        disableUrlParams: isNested,
    });
    const [sortQuery, setSortQuery] = useQueryString(
        "sort",
        generateSortString(
            sortableProperties.currentSortedProperty.name,
            sortableProperties.currentSortedProperty.isAscending
        ),
        {
            disableUrlParams: isNested,
        }
    );
    const [filterQuery, setFilterQuery] = useQueryString("filter", useDefaultFilter(), {
        isMultiple: true,
        disableUrlParams: isNested,
    });

    const sortParams = parseSortParams(sortQuery);

    const loadingTable = Array.apply(null, Array(+itemsPerPage));
    const [itemIdToSelectedMap, setItemIdToSelectedMap] = useState({});
    const [itemIdToOpenActionsPopoverMap, setItemIdToOpenActionsPopoverMap] = useState({});

    const [selectedToDeleteTripId, setSlectedToDeleteTripId] = useState(null);

    const history = useHistory();

    const isTripsLoading = tripsSlice.isLoading;

    const sortOptions = useMemo(() => generateSortOptions(columns), [columns]);

    const areAllItemsSelected = () => trips.findIndex((item) => !isItemSelected(item.id)) === -1;

    useEffect(() => {
        const hasInitSortProperty = sortOptions.find(
            (option) => option.name === initialSortablePropertyName
        );

        sortableProperties = new SortableProperties(
            [...sortOptions],
            hasInitSortProperty ? initialSortablePropertyName : sortOptions[0]?.name
        );
    }, [sortOptions]);

    useEffect(
        () => () => {
            // Create new instances of params constants
            initParamHelpers();
        },
        []
    );

    useDidUpdateEffect(() => {
        if (pageIndex !== 1) {
            setPageIndex(1);
        } else {
            fetchTrips();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterQuery, groupQuery, sortQuery]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => fetchTrips(), [pageIndex, itemsPerPage]);

    useEffect(() => {
        pager.setItemsPerPage(+itemsPerPage);
    }, [itemsPerPage]);

    useEffect(() => {
        pager.goToPageIndex(+pageIndex - 1);
    }, [pageIndex]);

    useEffect(() => {
        const totalItems = tripsSlice.meta?.totalRecordCount;

        pager.setTotalItems(totalItems);
        pager.goToPageIndex(+pageIndex - 1);

        setTotalRecordCount(totalItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripsSlice.meta?.totalRecordCount]);

    useEffect(() => {
        if (userIdKey === "" || userIdKey === null) {
            dispatch(logoutSuccess());
        }
    }, [dispatch, userIdKey]);

    const fetchTrips = () => {
        dispatch(
            fetchEntities(
                "trips",
                +pageIndex - 1,
                +itemsPerPage,
                sortParams.sortField,
                sortParams.sortDirection,
                null,
                includedEntitiesString,
                {
                    driver: "id,name",
                    truck: "id,plateNumber",
                    organization: "id,name",
                    tripPoints: "id,locationAttributes,tripId,position,pointType,cityName",
                    truckType: "id,alias",
                    supplier: "id,name",
                    tripType: "id,name",
                },
                filterQuery,
                groupQuery,
                url
            )
        );
    };

    const toggleAll = () => {
        const allSelected = areAllItemsSelected();
        const newItemIdToSelectedMap = {};
        trips.forEach((item) => (newItemIdToSelectedMap[item.id] = !allSelected));

        setItemIdToSelectedMap(newItemIdToSelectedMap);
    };

    const renderSelectAll = (mobile) => (
        <EuiCheckbox
            id="selectAllCheckbox"
            label={mobile ? "Select all" : null}
            checked={areAllItemsSelected()}
            onChange={toggleAll}
            type={mobile ? null : "inList"}
        />
    );

    const onSort = (sortField) => {
        sortableProperties.sortOn(sortField);

        const { isAscending } = sortableProperties.currentSortedProperty;
        const newSortString = generateSortString(sortField, isAscending);

        setSortQuery(newSortString);
    };

    const isItemSelected = (itemId) => itemIdToSelectedMap[itemId];
    const isPopoverOpen = (itemId) => itemIdToOpenActionsPopoverMap[itemId];

    const toggleItem = (itemId) => {
        setItemIdToSelectedMap((previousState) => {
            const newItemIdToSelectedMap = {
                ...previousState,
                [itemId]: !previousState[itemId],
            };

            return newItemIdToSelectedMap;
        });
    };
    const togglePopover = (itemId) => {
        setItemIdToOpenActionsPopoverMap((previousState) => {
            const newItemIdToOpenActionsPopoverMap = {
                ...previousState,
                [itemId]: !previousState[itemId],
            };

            return newItemIdToOpenActionsPopoverMap;
        });
    };
    const closePopover = (itemId) => {
        // only update the state if this item's popover is open
        if (isPopoverOpen(itemId)) {
            setItemIdToOpenActionsPopoverMap((previousState) => {
                const newItemIdToOpenActionsPopoverMap = {
                    ...previousState,
                    [itemId]: false,
                };

                return newItemIdToOpenActionsPopoverMap;
            });
        }
    };

    const goToTrip = (id) => {
        dispatch(clearCommentStates());
        history.push(`/database/trips/${id}`);
        closePopover();
    };

    // const deleteTrip = id => {
    //     setSlectedToDeleteTripId(id)
    //     closePopover()
    // }

    const destroyTrip = () => {
        dispatch(destroyEntity("trips", selectedToDeleteTripId));
        setSlectedToDeleteTripId(null);
    };

    const renderHeaderCells = () => {
        const headers = [];

        columns.forEach((column) => {
            if (column.isCheckbox) {
                headers.push(
                    <EuiTableHeaderCellCheckbox
                        key={column.id}
                        width={column.width}
                    >
                        {renderSelectAll()}
                    </EuiTableHeaderCellCheckbox>
                );
            } else {
                headers.push(
                    <EuiTableHeaderCell
                        key={column.id}
                        align={column.alignment}
                        width={column.width}
                        onSort={column.isSortable ? () => onSort(column.id) : undefined}
                        isSorted={sortParams.sortField === column.id}
                        isSortAscending={sortableProperties.isAscendingByName(column.id)}
                        mobileOptions={column.mobileOptions}
                    >
                        {intl.formatMessage({ id: `trip-attr.${column.id}` })}
                    </EuiTableHeaderCell>
                );
            }
        });
        return headers.length ? headers : null;
    };

    const renderRow = (item, index, groupKey) => {
        const cells = columns.map((column) => {
            if (isTripsLoading || _.isNil(item)) {
                return (
                    <EuiTableRowCell
                        key={column.id}
                        textOnly={false}
                    >
                        <S.TripCellLoadingContentWrapper
                            blockContent={
                                isTripsLoading &&
                                (column.id === "createdAt" || column.id === "tripPoints")
                            }
                        >
                            <EuiLoadingContent lines={1} />
                        </S.TripCellLoadingContentWrapper>
                    </EuiTableRowCell>
                );
            }

            const cell = item[column.id];
            let child;

            if (column.isCheckbox) {
                return (
                    <EuiTableRowCellCheckbox key={column.id}>
                        <EuiCheckbox
                            id={`${item.id}-checkbox`}
                            checked={isItemSelected(item.id)}
                            onChange={() => toggleItem(item.id)}
                            type="inList"
                        />
                    </EuiTableRowCellCheckbox>
                );
            }

            if (column.isActionsPopover) {
                return (
                    <EuiTableRowCell
                        key={column.id}
                        header={intl.formatMessage({ id: `trip-attr.${column.id}` })}
                        textOnly={false}
                        hasActions
                        align="right"
                    >
                        <EuiPopover
                            id={`${item.id}-actions`}
                            button={
                                <EuiButtonIcon
                                    aria-label="Actions"
                                    iconType="boxesHorizontal"
                                    size="s"
                                    color="text"
                                    onClick={() => togglePopover(item.id)}
                                />
                            }
                            isOpen={isPopoverOpen(item.id)}
                            closePopover={() => closePopover(item.id)}
                            panelPaddingSize="none"
                            anchorPosition="leftCenter"
                        >
                            <EuiContextMenuPanel
                                items={[
                                    <EuiContextMenuItem
                                        key="A"
                                        icon="pencil"
                                        onClick={() => {
                                            goToTrip(item.id);
                                        }}
                                    >
                                        {intl.formatMessage({ id: "misc.open" })}
                                    </EuiContextMenuItem>,
                                ]}
                            />
                        </EuiPopover>
                    </EuiTableRowCell>
                );
            }

            if (column.render) {
                const itemTripPoints = tripPoints.filter((point) => +point.tripId === +item.id);
                const itemTripTypes = tripTypes.filter((type) => +type.id === +item.tripTypeId);

                const relatedEntities =
                    column.id === "driverId"
                        ? drivers.find((d) => +d.id === cell) || {}
                        : column.id === "truckId"
                        ? trucks.find((t) => +t.id === cell) || {}
                        : column.id === "organizationId"
                        ? organizations.find((organization) => +organization.id === cell) || {}
                        : column.id === "supplierId"
                        ? suppliers.find((s) => +s.id === cell) || {}
                        : column.id === "tripPoints"
                        ? itemTripPoints.length && itemTripPoints
                        : column.id === "pickupTime"
                        ? item.pickupTime || {}
                        : {};

                child = column.render(
                    cell,
                    relatedEntities,
                    column.id === "tripPoints" ? item : undefined,
                    column.id === "tripPoints" ? fetchTrips : undefined,
                    column.id === "tripPoints" ? itemTripTypes && itemTripTypes[0] : undefined
                );
            } else if (column.cellProvider) {
                child = column.cellProvider(cell);
            } else if (cell.isLink) {
                child = <EuiLink href="">{cell.value}</EuiLink>;
            } else if (cell.truncateText) {
                child = cell.value;
            } else {
                child = cell;
            }

            return (
                <EuiTableRowCell
                    key={column.id}
                    align={column.alignment}
                    truncateText
                    textOnly={false}
                    mobileOptions={{
                        header: intl.formatMessage({ id: `trip-attr.${column.id}` }),
                        ...column.mobileOptions,
                    }}
                >
                    {child}
                </EuiTableRowCell>
            );
        });

        return (
            <>
                {groupQuery && index === 0 && !isTripsLoading && (
                    <EuiTitle
                        size="xs"
                        className="trip-table__group-title"
                    >
                        <h3>
                            <b>{!groupKey || groupKey === "undefined" ? "N/A" : groupKey}</b>
                        </h3>
                    </EuiTitle>
                )}
                <EuiTableRow
                    key={item ? item.id : index}
                    isSelected={item && isItemSelected(item.id)}
                    isSelectable={false}
                    hasActions
                >
                    {cells}
                </EuiTableRow>
            </>
        );
    };

    const renderTableRows = () => {
        if (isTripsLoading && !trips.length) {
            return loadingTable.map((item, index) => renderRow(item, index));
        }

        if (groupQuery) {
            const groupQueryKey = groupQuery.split(".")[0];
            const groupField = groupFields.find((field) => field.id === groupQueryKey);

            return _.map(_.groupBy(trips, groupField.key || groupField.id), (values, key) =>
                values.map((item, index) => {
                    const groupEntityName =
                        groupField.id === "creator"
                            ? `${_.camelCase(item.creatorType)}s`
                            : groupField.entityName;

                    const groupEntity = tripsSlice.included[groupEntityName];
                    const groupValue =
                        groupField.key && groupEntity
                            ? groupEntity[`_${key}`]?.attributes[
                                  groupField.displayName || groupField.name
                              ]
                            : key === "null"
                            ? "N/A"
                            : key;

                    return renderRow(item, index, groupValue);
                })
            ).flat();
        }

        return trips.map((item, index) => renderRow(item, index));
    };

    const getTableMobileSortItems = () => {
        const items = [];

        columns.forEach((column) => {
            if (column.isCheckbox || !column.isSortable) {
                return;
            }
            items.push({
                name: intl.formatMessage({ id: `trip-attr.${column.id}` }),
                key: column.id,
                onSort: () => onSort(column.id),
                isSorted: sortParams.sortField === column.id,
                isSortAscending: sortableProperties.isAscendingByName(column.id),
            });
        });

        return items.length ? items : null;
    };

    const onChangeItemsPerPage = (itemsPerPage) => setItemsPerPage(itemsPerPage);
    const onChangePage = (index) => setPageIndex(index + 1);

    const handlefilterChange = (filterQuery, filterString) => {
        setFilterQuery(filterQuery);
    };

    return (
        <>
            <S.TripTableWrapper>
                {!isNested && <EuiSpacer size="xl" />}
                <EuiFlexGroup>
                    <EuiFlexItem>
                        <TableFilter
                            filterFields={tripFilterFields.filter(
                                (field) => field.id !== relationKey
                            )}
                            onChange={handlefilterChange}
                            defaultValue={filterQuery}
                        />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <TableGroup
                            groupFields={groupFields}
                            onChange={(fieldId) => {
                                setGroupQuery(fieldId);
                            }}
                            defaultValue={groupQuery}
                        />
                    </EuiFlexItem>
                </EuiFlexGroup>

                <EuiSpacer size="m" />

                <TripColumnsSelector
                    selectedColumns={columns}
                    trip={trips[0]}
                    onChange={(selectedOptions) => {
                        setColumns(dropdownOptionsToColumns(selectedOptions, defaultColumns));
                    }}
                />

                <EuiTableHeaderMobile>
                    <EuiFlexGroup
                        responsive={false}
                        justifyContent="spaceBetween"
                        alignItems="baseline"
                    >
                        <EuiFlexItem grow={false}>
                            <EuiTableSortMobile items={getTableMobileSortItems()} />
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </EuiTableHeaderMobile>

                <EuiTable id={tableId}>
                    <EuiTableHeader>{renderHeaderCells()}</EuiTableHeader>

                    <EuiTableBody>{renderTableRows()}</EuiTableBody>
                </EuiTable>

                {!isTripsLoading && !trips.length && (
                    <EuiTextColor
                        component="div"
                        color="subdued"
                    >
                        <center> No trips in this page</center>
                    </EuiTextColor>
                )}

                <EuiTablePagination
                    aria-controls={tableId}
                    activePage={pager.getCurrentPageIndex()}
                    itemsPerPage={+itemsPerPage}
                    itemsPerPageOptions={[5, 10, 20]}
                    pageCount={pager.getTotalPages()}
                    onChangeItemsPerPage={onChangeItemsPerPage}
                    onChangePage={onChangePage}
                />
            </S.TripTableWrapper>
            {!_.isNil(selectedToDeleteTripId) && (
                <ConfirmationModal
                    title={`Delete Trip #${selectedToDeleteTripId}`}
                    content="Are you sure you want to delete this trip?"
                    onCancel={() => setSlectedToDeleteTripId(null)}
                    onConfirm={destroyTrip}
                />
            )}
        </>
    );
}

export default TripTable;
